/**
 * Vision Workflow Connection Validation Rules
 *
 * This module defines the data flow types and connection rules for vision nodes.
 * Uses a type-based validation system where each node declares what data types
 * it produces (outputs) and consumes (inputs).
 *
 * Benefits of this approach:
 * - Adding a new node only requires defining its I/O types
 * - Rules are automatically applied based on type compatibility
 * - Easy to extend with new data types
 * - Self-documenting: clear understanding of data flow
 */

/**
 * Data types that flow between vision nodes.
 * These represent the semantic meaning of the data, not the technical format.
 */
export enum VisionDataFlowType {
  /** Raw video frames from camera or file */
  VideoStream = "videoStream",

  /** Detection output: bounding boxes, class labels, confidence scores */
  Detections = "detections",

  /** Tracked objects: detections with persistent IDs across frames */
  TrackedObjects = "trackedObjects",

  /** Segmentation masks: pixel-level object boundaries */
  SegmentationMasks = "segmentationMasks",

  /** Analytics events: zone enter/exit, dwell time, counting events */
  AnalyticsEvents = "analyticsEvents",

  /** Aggregated metrics: counts, averages, occupancy over time windows */
  Metrics = "metrics",

  /** Generic storable data: can be stored in DB or exposed via API */
  GenericData = "genericData"
}

/**
 * Specification for a node's input/output capabilities.
 */
export interface NodeIOSpec {
  /** Data types this node can output (empty = sink node) */
  outputs: VisionDataFlowType[];
  /** Data types this node can accept as input (empty = source node) */
  accepts: VisionDataFlowType[];
  /** Human-readable description for tooltips */
  description: string;
  /** Category for grouping in UI */
  category: "Input Sources" | "AI Processing" | "Analytics" | "Outputs" | "Services";
  /** Whether this node type is temporarily disabled (default: false) */
  disabled?: boolean;
}

/**
 * Registry of all vision node types and their I/O specifications.
 *
 * To add a new node:
 * 1. Add its type key (must match EVisionNodeTypes value)
 * 2. Define outputs array (what data types it produces)
 * 3. Define accepts array (what data types it can receive)
 *
 * The validation system will automatically allow/disallow connections
 * based on type compatibility.
 */
export const NODE_IO_SPECS: Record<string, NodeIOSpec> = {
  // ============================================
  // START NODE - Visual indicator, connects ONLY to video input
  // ============================================
  startNode: {
    outputs: [], // Special case: handled separately in validation logic
    accepts: [], // Source node - no inputs
    description: "Visual start indicator for workflow",
    category: "Input Sources"
  },

  // ============================================
  // INPUT SOURCES - Generate data, no inputs
  // ============================================
  videoInput: {
    outputs: [VisionDataFlowType.VideoStream],
    accepts: [], // Can only accept from start node (handled separately)
    description: "Video source (camera, file, stream)",
    category: "Input Sources"
  },

  // ============================================
  // AI PROCESSING - Transform video/detections
  // ============================================
  detector: {
    outputs: [VisionDataFlowType.Detections],
    accepts: [VisionDataFlowType.VideoStream],
    description: "Object detection (YOLO, RT-DETR)",
    category: "AI Processing"
  },

  tracker: {
    outputs: [VisionDataFlowType.TrackedObjects],
    accepts: [VisionDataFlowType.Detections],
    description: "Multi-object tracking (StrongSORT, ByteTrack)",
    category: "AI Processing"
  },

  segmentation: {
    outputs: [VisionDataFlowType.SegmentationMasks],
    accepts: [VisionDataFlowType.VideoStream],
    description: "Instance segmentation (SAM 2)",
    category: "AI Processing"
  },

  // ============================================
  // ANALYTICS - Process tracked objects into events/metrics
  // ============================================
  analyticsProcessor: {
    outputs: [VisionDataFlowType.AnalyticsEvents],
    accepts: [VisionDataFlowType.TrackedObjects, VisionDataFlowType.SegmentationMasks],
    description: "Zone-based analytics (enter, exit, dwell)",
    category: "Analytics"
  },

  metricsCalculator: {
    outputs: [VisionDataFlowType.Metrics],
    accepts: [VisionDataFlowType.AnalyticsEvents],
    description: "Time-windowed metric aggregation",
    category: "Analytics",
    disabled: true // TODO: Implement in future
  },

  // ============================================
  // OUTPUTS - Consume data, no outputs (sinks)
  // ============================================
  dashboardOutput: {
    outputs: [], // Sink node
    accepts: [VisionDataFlowType.Metrics, VisionDataFlowType.AnalyticsEvents],
    description: "Real-time dashboard visualization",
    category: "Outputs",
    disabled: true // TODO: Implement in future
  },

  reportGenerator: {
    outputs: [], // Sink node
    accepts: [VisionDataFlowType.Metrics, VisionDataFlowType.AnalyticsEvents],
    description: "Scheduled report generation (PDF, Excel)",
    category: "Outputs",
    disabled: true // TODO: Implement in future
  },

  // ============================================
  // SERVICES - Store/expose data
  // ============================================
  databaseService: {
    outputs: [VisionDataFlowType.GenericData], // Can be queried by API
    accepts: [VisionDataFlowType.Metrics, VisionDataFlowType.AnalyticsEvents, VisionDataFlowType.TrackedObjects],
    description: "Persistent data storage (PostgreSQL, MongoDB)",
    category: "Services"
  },

  apiGateway: {
    outputs: [], // External sink
    accepts: [VisionDataFlowType.Metrics, VisionDataFlowType.AnalyticsEvents, VisionDataFlowType.GenericData],
    description: "REST/WebSocket API endpoints",
    category: "Services",
    disabled: true // TODO: Implement in future
  }
};

/**
 * Check if a connection between two nodes is valid.
 *
 * @param sourceNodeType - The type of the source (output) node
 * @param targetNodeType - The type of the target (input) node
 * @returns Object with validity status, short message, and detailed reason
 */
export function isValidConnection(
  sourceNodeType: string,
  targetNodeType: string
): { valid: boolean; shortMessage: string; detail: string } {
  // Special case: startNode can ONLY connect to videoInput
  if (sourceNodeType === "startNode") {
    if (targetNodeType === "videoInput") {
      return {
        valid: true,
        shortMessage: "Valid connection",
        detail: "Start node connects to Video Input to begin the workflow."
      };
    }
    return {
      valid: false,
      shortMessage: "Start node can only connect to Video Input",
      detail: "The start node must connect to a Video Input node to begin the workflow pipeline."
    };
  }

  const sourceSpec = NODE_IO_SPECS[sourceNodeType];
  const targetSpec = NODE_IO_SPECS[targetNodeType];

  // Check if node types are known
  if (!sourceSpec) {
    return {
      valid: false,
      shortMessage: "Unknown source node type",
      detail: `The source node type "${sourceNodeType}" is not recognized.`
    };
  }

  if (!targetSpec) {
    return {
      valid: false,
      shortMessage: "Unknown target node type",
      detail: `The target node type "${targetNodeType}" is not recognized.`
    };
  }

  // Helper to get friendly names
  const getNodeName = (type: string): string => {
    const names: Record<string, string> = {
      videoInput: "Video Input",
      detector: "Detector",
      tracker: "Tracker",
      segmentation: "Segmentation",
      analyticsProcessor: "Analytics Processor",
      metricsCalculator: "Metrics Calculator",
      dashboardOutput: "Dashboard Output",
      reportGenerator: "Report Generator",
      databaseService: "Database Service",
      apiGateway: "API Gateway"
    };
    return names[type] || type;
  };

  const sourceName = getNodeName(sourceNodeType);
  const targetName = getNodeName(targetNodeType);

  // Check if source has outputs
  if (sourceSpec.outputs.length === 0) {
    return {
      valid: false,
      shortMessage: `${sourceName} cannot be connected to ${targetName}`,
      detail: `${sourceName} is an output node and cannot have outgoing connections.`
    };
  }

  // Check if target accepts inputs
  if (targetSpec.accepts.length === 0) {
    return {
      valid: false,
      shortMessage: `${sourceName} cannot be connected to ${targetName}`,
      detail: `${targetName} is an input source node and cannot receive incoming connections.`
    };
  }

  // Check if any output type matches any accepted input type
  const matchingTypes = sourceSpec.outputs.filter((outputType) => targetSpec.accepts.includes(outputType));

  if (matchingTypes.length === 0) {
    // Create user-friendly data type names
    const formatDataTypes = (types: VisionDataFlowType[]): string => {
      const labels: Record<string, string> = {
        videoStream: "Video Stream",
        detections: "Detections",
        trackedObjects: "Tracked Objects",
        segmentationMasks: "Segmentation Masks",
        analyticsEvents: "Analytics Events",
        metrics: "Metrics",
        genericData: "Generic Data"
      };
      return types.map((t) => labels[t] || t).join(", ");
    };

    const outputTypeNames = formatDataTypes(sourceSpec.outputs);
    const acceptTypeNames = formatDataTypes(targetSpec.accepts);
    return {
      valid: false,
      shortMessage: `${sourceName} cannot be connected to ${targetName}`,
      detail: `${sourceName} outputs ${outputTypeNames}, but ${targetName} requires ${acceptTypeNames}.`
    };
  }

  return {
    valid: true,
    shortMessage: "Valid connection",
    detail: `Data type: ${matchingTypes.join(", ")}`
  };
}

/**
 * Get list of node types that can connect TO the given node type.
 * Useful for showing valid source options in UI.
 */
export function getValidSourcesFor(targetNodeType: string): string[] {
  // Special case: only startNode can connect to videoInput
  if (targetNodeType === "videoInput") {
    return ["startNode"];
  }

  const targetSpec = NODE_IO_SPECS[targetNodeType];
  if (!targetSpec || targetSpec.accepts.length === 0) {
    return [];
  }

  return Object.entries(NODE_IO_SPECS)
    .filter(([nodeType, sourceSpec]) => {
      // Exclude startNode as it's handled specially
      if (nodeType === "startNode") return false;
      // Check if source outputs any type that target accepts
      return sourceSpec.outputs.some((outputType) => targetSpec.accepts.includes(outputType));
    })
    .map(([nodeType]) => nodeType);
}

/**
 * Get list of node types that the given node can connect TO.
 * Useful for showing valid target options in UI.
 */
export function getValidTargetsFor(sourceNodeType: string): string[] {
  // Special case: startNode can only connect to videoInput
  if (sourceNodeType === "startNode") {
    return ["videoInput"];
  }

  const sourceSpec = NODE_IO_SPECS[sourceNodeType];
  if (!sourceSpec || sourceSpec.outputs.length === 0) {
    return [];
  }

  return Object.entries(NODE_IO_SPECS)
    .filter(([nodeType, targetSpec]) => {
      // Exclude videoInput as it only accepts from startNode
      if (nodeType === "videoInput") return false;
      // Check if target accepts any type that source outputs
      return targetSpec.accepts.some((acceptType) => sourceSpec.outputs.includes(acceptType));
    })
    .map(([nodeType]) => nodeType);
}

/**
 * Get a human-readable description of what data flows between nodes.
 */
export function getConnectionDataType(sourceNodeType: string, targetNodeType: string): string | null {
  const sourceSpec = NODE_IO_SPECS[sourceNodeType];
  const targetSpec = NODE_IO_SPECS[targetNodeType];

  if (!sourceSpec || !targetSpec) {
    return null;
  }

  const matchingTypes = sourceSpec.outputs.filter((outputType) => targetSpec.accepts.includes(outputType));

  return matchingTypes.length > 0 ? matchingTypes[0] : null;
}

/**
 * Validate an entire workflow and return all validation errors.
 */
export function validateWorkflow(
  nodes: { id: string; type: string }[],
  edges: { source: string; target: string }[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const nodeMap = new Map(nodes.map((n) => [n.id, n.type]));

  for (const edge of edges) {
    const sourceType = nodeMap.get(edge.source);
    const targetType = nodeMap.get(edge.target);

    if (!sourceType || !targetType) {
      errors.push(`Edge references unknown node: ${edge.source} → ${edge.target}`);
      continue;
    }

    const result = isValidConnection(sourceType, targetType);
    if (!result.valid) {
      errors.push(`Invalid connection ${edge.source} → ${edge.target}: ${result.shortMessage}. ${result.detail}`);
    }
  }

  // Check for disconnected source nodes (warnings, not errors)
  const connectedSources = new Set(edges.map((e) => e.source));
  const sourceNodes = nodes.filter((n) => {
    const spec = NODE_IO_SPECS[n.type];
    return spec && spec.outputs.length > 0 && spec.accepts.length === 0;
  });

  for (const node of sourceNodes) {
    if (!connectedSources.has(node.id)) {
      // This is a warning, not an error - source node without connections
      // errors.push(`Warning: Source node ${node.id} (${node.type}) has no outgoing connections`);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get human-readable label for a data flow type.
 */
export function getDataFlowTypeLabel(type: VisionDataFlowType): string {
  const labels: Record<VisionDataFlowType, string> = {
    [VisionDataFlowType.VideoStream]: "Video Stream",
    [VisionDataFlowType.Detections]: "Detections",
    [VisionDataFlowType.TrackedObjects]: "Tracked Objects",
    [VisionDataFlowType.SegmentationMasks]: "Segmentation Masks",
    [VisionDataFlowType.AnalyticsEvents]: "Analytics Events",
    [VisionDataFlowType.Metrics]: "Metrics",
    [VisionDataFlowType.GenericData]: "Generic Data"
  };
  return labels[type] || type;
}

/**
 * Get human-readable display name for a node type.
 */
export function getNodeDisplayName(nodeType: string): string {
  const names: Record<string, string> = {
    videoInput: "Video Input",
    detector: "Detector",
    tracker: "Tracker",
    segmentation: "Segmentation",
    analyticsProcessor: "Analytics Processor",
    metricsCalculator: "Metrics Calculator",
    dashboardOutput: "Dashboard Output",
    reportGenerator: "Report Generator",
    databaseService: "Database Service",
    apiGateway: "API Gateway"
  };
  return names[nodeType] || nodeType;
}

/**
 * Get all valid target node types that can connect to a given source node type.
 *
 * @param sourceNodeType - The type of the source node
 * @returns Array of valid target node types with their display names and categories
 */
export function getValidTargetNodes(sourceNodeType: string): Array<{
  nodeType: string;
  displayName: string;
  category: string;
  description: string;
}> {
  // Special case: startNode can only connect to videoInput
  if (sourceNodeType === "startNode") {
    const videoInputSpec = NODE_IO_SPECS.videoInput;
    return [
      {
        nodeType: "videoInput",
        displayName: "Video Input",
        category: videoInputSpec.category,
        description: videoInputSpec.description
      }
    ];
  }

  const sourceSpec = NODE_IO_SPECS[sourceNodeType];
  if (!sourceSpec || sourceSpec.outputs.length === 0) {
    return [];
  }

  const validTargets: Array<{
    nodeType: string;
    displayName: string;
    category: string;
    description: string;
  }> = [];

  // Check each possible target node type
  for (const [targetNodeType, targetSpec] of Object.entries(NODE_IO_SPECS)) {
    // Skip self-connection, source nodes (no inputs), videoInput (only for startNode), and disabled nodes
    if (
      targetNodeType === sourceNodeType ||
      targetNodeType === "videoInput" ||
      targetSpec.accepts.length === 0 ||
      targetSpec.disabled
    ) {
      continue;
    }

    // Check if any output type matches any accepted input type
    const hasCompatibleTypes = sourceSpec.outputs.some((outputType) => targetSpec.accepts.includes(outputType));

    if (hasCompatibleTypes) {
      validTargets.push({
        nodeType: targetNodeType,
        displayName: getNodeDisplayName(targetNodeType),
        category: targetSpec.category,
        description: targetSpec.description
      });
    }
  }

  // Sort by category, then by display name
  return validTargets.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return a.displayName.localeCompare(b.displayName);
  });
}
